import type {
  JoinRoomRequest,
  PublicRoomSummary,
  RoomSessionResponse,
} from "@secret-cabinet/contracts";

const apiBaseUrl = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");

export class ApiError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.code = code;
  }
}

export function createTvRoom(): Promise<RoomSessionResponse> {
  return request<RoomSessionResponse>("/api/rooms", {
    method: "POST",
    body: JSON.stringify({ tv: true }),
  });
}

export function joinRoom(code: string, input: JoinRoomRequest): Promise<RoomSessionResponse> {
  return request<RoomSessionResponse>(`/api/rooms/${encodeURIComponent(code)}/join`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function roomSummary(code: string): Promise<PublicRoomSummary> {
  return request<PublicRoomSummary>(`/api/rooms/${encodeURIComponent(code)}`);
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl}${path}`, {
      ...init,
      headers: {
        "content-type": "application/json",
        ...init.headers,
      },
    });
  } catch {
    throw new ApiError("network-unavailable", "The chamber server could not be reached.");
  }

  const payload = (await response.json().catch(() => null)) as
    | { code?: string; message?: string }
    | T
    | null;
  if (!response.ok) {
    const error = payload as { code?: string; message?: string } | null;
    throw new ApiError(
      error?.code ?? "request-failed",
      error?.message ?? "The chamber could not complete that request.",
    );
  }
  return payload as T;
}
